import React, { useState } from "react";
import "./SimuladorWorkbenchPage.css";
import { GROUP_BY_OPTIONS } from "../constants/defaults";
import { formatDateTime } from "../utils/formatters";
import useSimulationWorkbench from "../hooks/useSimulationWorkbench";
import ScenarioToolbar from "../components/ScenarioToolbar";
import QuickAdjustPanel from "../components/QuickAdjustPanel";
import FilterPanel from "../components/FilterPanel";
import KPIGrid from "../components/KPIGrid";
import ResultsTable from "../components/ResultsTable";
import ScenarioComparisonPanel from "../components/ScenarioComparisonPanel";
import SavedViewsPanel from "../components/SavedViewsPanel";
import ExecutionHistoryPanel from "../components/ExecutionHistoryPanel";
import WorkbenchTabs from "../components/WorkbenchTabs";
import SimulationBasesPanel from "../components/SimulationBasesPanel";

export default function SimuladorWorkbenchPage({ onBack }) {
  const workbench = useSimulationWorkbench();
  const { dataCatalog, scenarios, views, ui, results, bases } = workbench;
  const [activeTab, setActiveTab] = useState("workbench");

  const activeScenario = scenarios.active;

  if (!activeScenario) {
    return (
      <div className="swb-page">
        <div className="swb-panel">Nenhum cenário disponível.</div>
      </div>
    );
  }

  return (
    <div className="swb-page">
      <header className="swb-page__hero">
        <div>
          <div className="swb-page__eyebrow">Workbench de Simulação</div>
          <h1>Capacidade x Plano (MVP Front Mock)</h1>
          <p>
            Feature isolada para validar fluxo, UX e arquitetura de cenários, visões e
            simulação local sem backend.
          </p>
        </div>

        <div className="swb-page__hero-actions">
          {typeof onBack === "function" ? (
            <button type="button" className="swb-btn" onClick={onBack}>
              Voltar ao menu
            </button>
          ) : null}
          <button
            type="button"
            className="swb-btn swb-btn--primary"
            onClick={scenarios.run}
          >
            Rodar simulação
          </button>
        </div>
      </header>

      <div className="swb-layout">
        <aside className="swb-sidebar">
          <WorkbenchTabs activeTab={activeTab} onChange={setActiveTab} />

          {activeTab === "workbench" ? (
            <>
          <FilterPanel
            filters={ui.filters}
            options={dataCatalog.filterOptions}
            onChange={ui.updateFilter}
            onReset={ui.resetFilters}
          />

          <section className="swb-panel">
            <div className="swb-panel__head">
              <h3>Visualização</h3>
              <span className="swb-subtle">Layout local (mock)</span>
            </div>

            <div className="swb-fields-grid">
              <label className="swb-field swb-field--full">
                <span>Agrupar resultados</span>
                <select
                  value={ui.layout.groupBy}
                  onChange={(e) => ui.setGroupBy(e.target.value)}
                >
                  {GROUP_BY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="swb-summary-grid">
              <div className="swb-summary-item">
                <span>Máquinas (mock)</span>
                <strong>{dataCatalog.machines.length}</strong>
              </div>
              <div className="swb-summary-item">
                <span>Períodos mock</span>
                <strong>{dataCatalog.periods.length}</strong>
              </div>
              <div className="swb-summary-item">
                <span>Operadores recomendados</span>
                <strong>{dataCatalog.operators.summary.recommendedOperators}</strong>
              </div>
              <div className="swb-summary-item">
                <span>Manutencoes cadastradas</span>
                <strong>{dataCatalog.maintenanceSchedule.length}</strong>
              </div>
              <div className="swb-summary-item">
                <span>Última execução (ativa)</span>
                <strong>{formatDateTime(results.lastRun?.ranAt)}</strong>
              </div>
            </div>
          </section>

          <SavedViewsPanel
            views={views.list}
            activeViewId={views.activeId}
            onApplyView={views.apply}
            onSaveAsView={views.saveAs}
            onUpdateCurrentView={views.updateCurrent}
            onDeleteView={views.remove}
          />
            </>
          ) : (
            <section className="swb-panel">
              <div className="swb-panel__head">
                <h3>Modo Bases</h3>
                <span className="swb-subtle">Cadastre maquinas, equipes e manutencoes</span>
              </div>
              <p className="swb-subtle">
                As alteracoes marcam os cenarios como desatualizados. Volte para a aba Workbench e rode novamente.
              </p>
            </section>
          )}
        </aside>

        <main className="swb-main">
          {activeTab === "workbench" ? (
            <>
          <ScenarioToolbar
            activeScenario={activeScenario}
            scenarios={scenarios.list}
            periods={dataCatalog.periods}
            isOutdated={results.isOutdated}
            onSelectScenario={scenarios.select}
            onCreateScenario={scenarios.create}
            onDuplicateScenario={scenarios.duplicate}
            onRenameScenario={scenarios.rename}
            onDeleteScenario={scenarios.remove}
            onToggleScenarioKind={scenarios.setKind}
            onChangePeriod={scenarios.setPeriod}
            onRunSimulation={scenarios.run}
          />

          <QuickAdjustPanel
            parameters={activeScenario.parameters}
            onChange={scenarios.updateParameters}
            onRun={scenarios.run}
          />

          <KPIGrid aggregate={results.aggregate} isOutdated={results.isOutdated} />

          <ResultsTable
            rows={results.tableRows}
            visibleColumns={ui.layout.visibleColumns}
            allColumns={dataCatalog.resultColumns}
            groupBy={ui.layout.groupBy}
            onToggleColumn={ui.toggleVisibleColumn}
          />

          <div className="swb-two-col">
            <ScenarioComparisonPanel
              scenarioSummaries={scenarios.summaries}
              activeScenarioId={scenarios.activeId}
              compareScenarioId={ui.compareScenarioId}
              onChangeCompareScenario={ui.setCompareScenarioId}
              comparison={results.comparison}
            />

            <ExecutionHistoryPanel history={results.executionHistory} />
          </div>
            </>
          ) : (
            <SimulationBasesPanel bases={bases} periods={dataCatalog.periods} />
          )}
        </main>
      </div>
    </div>
  );
}
